import {ListBulletIcon, MapIcon} from '@heroicons/react/20/solid';
import classNames from 'classnames';
import {GetStaticProps, InferGetStaticPropsType} from 'next';
import {useRouter} from 'next/router';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import Col from '../../../features/common/Col';
import {useEnvironment, useFirebaseAuth} from '../../../features/common/EnvironmentProvider';
import Row from '../../../features/common/Row';
import {TWENTY_FOUR_HOURS_IN_SECONDS} from '../../../features/common/util';
import {useBreakpoint} from '../../../features/common/util/useBreakpoint';
import {MarketplaceCropType, MarketplaceServiceType, Point, ProductSearchParams} from '../../../features/query/graphql';
import {
  useCanBecomeSupplier,
  useFindSuppliers,
  useIsSupplier,
  useOwnSupplierStatus,
} from '../../../features/query/supplierHooks';
import Button from '../../../features/tool/components/buttons/Button';
import {getToolLayoutFullHeightExceptMobile} from '../../../features/tool/components/Layout';
import Map from '../../../features/tool/components/Map';
import EditYourSupplierBanner from '../../../features/tool/components/marketplace/EditYourSupplierBanner';
import LocationInput from '../../../features/tool/components/marketplace/LocationInput';
import ProximityDropdown from '../../../features/tool/components/marketplace/ProximityDropdown';
import SearchByCropTypeBox from '../../../features/tool/components/marketplace/SearchByCropTypeBox';
import SearchByServiceTypeBox from '../../../features/tool/components/marketplace/SearchByServiceTypeBox';
import SortDropdown from '../../../features/tool/components/marketplace/SortDropdown';
import SupplierCard from '../../../features/tool/components/marketplace/SupplierCard';
import {ExtendedSupplier, SortOrder} from '../../../features/tool/marketplace/types';
import {
  distanceInMiles,
  milesToMeters,
  queryToSupplierSearchParams,
  supplierSearchParamsToQuery,
} from '../../../features/tool/marketplace/util';
import {
  getApprovedExtendedSuppliers,
  getMarketplaceCropTypeList,
  getMarketplaceServiceTypeList,
} from '../../../features/tool/marketplace/util.server';
import {buildStaticPropsResponse} from '../../../features/tool/util/dataFetch';
import {MarketplaceOutlineIcon, Plant5LeavesIcon, Spinner} from '../../../resources/Icons';
import URLS from '../../../urls';
import {NextPageWithLayout} from '../../_app';

const SupplierListingPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = ({
  suppliers,
  allCrops,
  allServices,
}) => {
  const environment = useEnvironment();
  const router = useRouter();
  const {isMobile} = useBreakpoint('mobile');
  const searchParams = useMemo(() => queryToSupplierSearchParams(router.query), [router.query]);
  const [showMap, setShowMap] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const firebaseUser = useFirebaseAuth()?.currentUser;
  const isSupplier = useIsSupplier();
  const ownSupplierStatus = useOwnSupplierStatus();
  const canBecomeSupplier = useCanBecomeSupplier();

  const {location, proximity, sort, serviceTypes, cropTypes} = searchParams;

  const searchEnabled = !!serviceTypes?.length || !!cropTypes?.length;

  const filteredSuppliers = useMemo(
    () =>
      !location || !proximity
        ? [...suppliers]
        : suppliers.filter(supplier => {
            const {latitude, longitude} = supplier;
            if (latitude === null || longitude === null) return false;
            return distanceInMiles({latitude, longitude}, location) <= proximity;
          }),
    [location, proximity, suppliers]
  );

  const querySearchParams = {cropTypes, serviceTypes, suppliers: filteredSuppliers.map(s => s.slug)};

  const {
    data: searchData,
    isFetching: searchLoading,
    error: searchError,
  } = useFindSuppliers({searchParams: querySearchParams}, {enabled: searchEnabled});

  const searchedSuppliers = useMemo(
    () => (searchEnabled ? suppliers.filter(s => searchData?.supplierSearch.includes(s.slug)) : filteredSuppliers),
    [filteredSuppliers, searchData?.supplierSearch, searchEnabled, suppliers]
  );

  const sortedSuppliers = useMemo(
    () =>
      searchedSuppliers.sort((a, b) => {
        const averageA = a.rating?.average ?? 0;
        const averageB = b.rating?.average ?? 0;
        switch (sort) {
          case 'rating':
            return averageB - averageA;
          case '-rating':
            return averageA - averageB;
          case 'name':
            return (a.name ?? '').localeCompare(b.name ?? '');
          case '-name':
            return (b.name ?? '').localeCompare(a.name ?? '');
          default:
            return 0;
        }
      }),
    [searchedSuppliers, sort]
  );

  const spots = useMemo(
    () =>
      sortedSuppliers
        .map(supplier => {
          const {latitude, longitude} = supplier;
          if (latitude === null || longitude === null) return null;
          return {
            lat: latitude,
            lng: longitude,
            selected: selectedId === supplier.slug,
          };
        })
        .filter(Boolean) as {lat: number; lng: number; selected: boolean}[],
    [selectedId, sortedSuppliers]
  );

  const handleSpotClick = useCallback(
    (spotIdx: number) => {
      setLoading(true);
      setShowMap(false);
      setSelectedId(sortedSuppliers[spotIdx].slug);

      setTimeout(() => {
        const card = cardContainerRef.current?.children[spotIdx];
        if (!(card instanceof HTMLElement)) return;
        if (isMobile) {
          card.scrollIntoView({behavior: 'smooth', block: 'nearest'});
        } else {
          // Using scrollTo as scrollIntoView leaves borders out, and Chrome was not respecting scroll-margin-top
          cardContainerRef.current?.scrollTo({top: card.offsetTop, behavior: 'smooth'});
        }
        setLoading(false);
      }, 0); // Execute on next cycle to let the card grow first
    },
    [isMobile, sortedSuppliers]
  );

  const updateQuery = useCallback(
    (newParams: Partial<ProductSearchParams>) => {
      void router.push(
        {query: supplierSearchParamsToQuery({...searchParams, ...newParams, page: undefined})},
        undefined,
        {
          shallow: true,
        }
      );
    },
    [router, searchParams]
  );

  const handleChangeLocation = useCallback(
    (postcode?: string, location?: Point) => {
      const proximity = searchParams.proximity ?? 50;
      updateQuery({postcode, location, proximity});
    },
    [searchParams.proximity, updateQuery]
  );

  const handleChangeProximity = useCallback(
    (value: number) => updateQuery({proximity: value < 0 ? undefined : value}),
    [updateQuery]
  );

  const handleChangeCropType = useCallback(
    (cropIds: string[]) => {
      updateQuery({cropTypes: cropIds});
    },
    [updateQuery]
  );

  const handleChangeServiceType = useCallback(
    (serviceIds: string[]) => {
      updateQuery({serviceTypes: serviceIds});
    },
    [updateQuery]
  );

  const handleSortOrderChange = useCallback(
    (value: keyof typeof SortOrder) => updateQuery({sort: value}),
    [updateQuery]
  );

  useEffect(() => {
    setSelectedId(null);
  }, [searchParams]);

  const mapLocation = useMemo(
    () =>
      searchParams.location ? {lat: searchParams.location.latitude, lng: searchParams.location.longitude} : undefined,
    [searchParams.location]
  );

  const sortKeys: (keyof typeof SortOrder)[] = ['name', '-name', 'rating', '-rating'];

  return (
    <>
      {isSupplier && <EditYourSupplierBanner status={ownSupplierStatus} />}
      <Col className="grow w-full max-w-wider min-h-0 mx-auto mt-lg px-sm gap-sm overflow-x-hidden">
        {/* Heading row*/}
        <Row className={'items-center justify-between gap-xs flex-wrap mobile:flex-col mobile:items-start'}>
          <h1 className="grow font-semibold text-3xl leading-tight">Find biomass crops suppliers</h1>
          <Row
            className={classNames(
              'gap-sm whitespace-nowrap',
              'mobile:w-[calc(100%+40px)] overflow-x-scroll mobile:-ml-sm mobile:px-sm'
            )}
          >
            {canBecomeSupplier && (
              <Button
                variant={'prominent'}
                className={'min-w-fit m-[2px]'}
                accessory={<Plant5LeavesIcon className={'w-[1.4rem] flex-none'} />}
                href={URLS.commonApp.supplier.about}
              >
                Become a supplier
              </Button>
            )}
            <Button
              variant={'secondary'}
              className={'min-w-fit m-[2px]'}
              accessory={<MarketplaceOutlineIcon className={'w-[1.4rem] flex-none'} />}
              href={URLS.marketPlace.products}
            >
              Search marketplace
            </Button>
          </Row>
        </Row>
        {/* Filters row*/}
        <Row className={'gap-sm flex-wrap'}>
          <Row className={'grow gap-sm'}>
            <LocationInput
              postcode={searchParams.postcode ?? undefined}
              location={searchParams.location ?? undefined}
              onChange={handleChangeLocation}
            />
            {isMobile && (
              <Button
                variant={'primary'}
                accessory={
                  showMap ? (
                    <ListBulletIcon className={'w-6 h-6 text-white'} />
                  ) : (
                    <MapIcon className={'w-6 h-6 text-white'} />
                  )
                }
                onClick={() => setShowMap(!showMap)}
                className={'shrink-0 whitespace-nowrap'}
              >
                {showMap ? 'List view' : 'Map view'}
              </Button>
            )}
          </Row>
          <Row
            className={classNames(
              'gap-sm min-w-0 whitespace-nowrap',
              'mobile:w-[calc(100%+40px)] mobile:overflow-x-scroll mobile:-mx-sm mobile:px-sm mobile:py-[2px] mobile:-my-[2px]'
            )}
          >
            <ProximityDropdown
              proximity={searchParams.proximity ?? -1}
              disabled={!searchParams.postcode && !searchParams.location}
              onChange={handleChangeProximity}
              asModal={isMobile}
            />
            <SearchByServiceTypeBox.Dropdown
              allServices={allServices}
              selectedIds={serviceTypes ?? []}
              onChange={handleChangeServiceType}
              className={'w-[200px]'}
              noSelectionLabel={'Service type'}
              asModal={isMobile}
              withClearAll
            />
            <SearchByCropTypeBox.Dropdown
              allCrops={allCrops}
              selectedIds={cropTypes ?? []}
              onChange={handleChangeCropType}
              className={'w-[200px]'}
              noSelectionLabel={'Crop type'}
              asModal={isMobile}
              withClearAll
            />
          </Row>
        </Row>
        {/* Results row */}
        <Row className={classNames('min-h-0 grow w-full gap-x-sm', 'mobile:!flex-col')}>
          {/* List view*/}
          <Col className={'w-2/3 mobile:w-full'}>
            {/* Result count and sorting */}
            {!!searchError && <div className={'text-sm text-red-200'}>{String(searchError)}</div>}
            {!searchError && (
              <Row className={'text-sm items-start relative'}>
                {searchLoading && (
                  <div className={'absolute inset-0 bg-white'}>
                    <Spinner className={'mt-sm mx-auto'} />
                  </div>
                )}
                <Row className={'grow justify-between items-center'}>
                  <span>
                    {sortedSuppliers.length} {sortedSuppliers.length == 1 ? 'result' : 'results'}
                  </span>
                  <SortDropdown
                    order={searchParams.sort ?? undefined}
                    options={sortKeys}
                    onChange={handleSortOrderChange}
                    className={classNames((searchLoading || (isMobile && showMap)) && 'invisible')}
                  />
                </Row>
              </Row>
            )}
            {/* Supplier cards*/}
            {!searchError && (
              <div
                ref={cardContainerRef}
                className={classNames(
                  'relative grid grid-cols-2 mobile:grid-cols-1 auto-rows-max gap-xs overflow-y-auto -mx-[2px] pt-[2px] px-[2px] pb-sm',
                  showMap && 'mobile:hidden'
                )}
              >
                {sortedSuppliers.map(supplier => (
                  <SupplierCard
                    key={supplier.slug}
                    className={'shrink-0 scroll-mt-[200px]'}
                    supplier={supplier}
                    selected={selectedId === supplier.slug}
                    onSelected={() => setSelectedId(supplier.slug)}
                    disabled={loading}
                    onOpen={() => router.push(URLS.marketPlace.supplier(supplier.slug).about)}
                    showEditButton={!!firebaseUser && firebaseUser?.uid === supplier.owner_id}
                    onShowInMap={isMobile ? () => setShowMap(true) : undefined}
                  />
                ))}
              </div>
            )}
          </Col>
          {/* Map view */}
          <Col className={classNames('grow mb-sm gap-sm mobile:h-[60vh]', !showMap && 'mobile:hidden')}>
            <Map
              style={{flexGrow: 1}}
              googleMapsApiKey={environment?.googleMaps.apiKey ?? ''}
              zoom={6}
              spots={spots}
              onSpotClick={handleSpotClick}
              currentLocation={mapLocation}
              radius={searchParams.proximity ? milesToMeters(searchParams.proximity) : undefined}
            />
          </Col>
        </Row>
      </Col>
    </>
  );
};

export const getStaticProps: GetStaticProps<{
  suppliers: ExtendedSupplier[];
  allCrops: MarketplaceCropType[];
  allServices: MarketplaceServiceType[];
}> = async () => {
  return buildStaticPropsResponse(
    {
      suppliers: await getApprovedExtendedSuppliers(),
      allServices: await getMarketplaceServiceTypeList(),
      allCrops: await getMarketplaceCropTypeList(),
    },
    TWENTY_FOUR_HOURS_IN_SECONDS
  );
};

export default SupplierListingPage;

SupplierListingPage.getLayout = getToolLayoutFullHeightExceptMobile;

SupplierListingPage.title = 'Find suppliers';
